import { useEffect, useState } from 'react';
import { X, ChevronDown, Settings } from 'lucide-react';
import { DataSource, MapDefinition, MapLayerToggle } from '../../api/mapping/types';
import { MappingService } from '../../api/mapping';
import { getModelInfo } from './mapUtils';
import { useDemo } from '../demo/DemoProvider';
import { AVAILABLE_MODELS } from './options';
import {
  getCachedAvailableForecasts,
  subscribeToAvailableForecasts,
} from './availableForecastsCache';

export interface LayersPanelProps {
  readonly title: string;
  readonly isDark: boolean;
  readonly allowSwitchDataSource: boolean;
  readonly currentDataSource: DataSource;
  readonly currentToggles: MapLayerToggle[];
  readonly activeToggleIds: Set<string>;
  readonly onClose: () => void;
  readonly onToggleChange: (toggleTitle: string, isActive: boolean) => void;
  readonly globalOverlayOpacity: number;
  readonly setGlobalOverlayOpacity: (value: number) => void;
  readonly currentMapId: number;
  readonly setCurrentMap: (map: MapDefinition) => void;
  readonly setCurrentDataSource: (dataSource: DataSource) => void;
  readonly onSettingsClick: () => void;
  readonly availableVariables?: string[];
}

function classNames(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

interface BaseLayerDropdownProps {
  readonly isDark: boolean;
  readonly currentMapId: number;
  readonly title: string;
  readonly onChange: (id: number) => void;
  readonly isOpen: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly availableVariables?: string[];
}

function BaseLayerDropdown({
  isDark,
  currentMapId,
  title,
  onChange,
  isOpen,
  onOpenChange,
  availableVariables,
}: BaseLayerDropdownProps) {
  const selected = MappingService.availableMaps.find((m) => m.id === currentMapId);

  // Helper function to check if the primary variable for a map is available
  const isMapAvailable = (map: MapDefinition): boolean => {
    if (!availableVariables) return true; // If no info, assume available
    // Only check the primary variable - all other layers are optional overlays
    return availableVariables.includes(map.primaryVariable);
  };

  return (
    <div className="relative">
      <button
        onClick={() => onOpenChange(!isOpen)}
        className={classNames(
          'w-full px-2 py-1 rounded-md border flex items-center justify-between text-left transition-colors focus:outline-none focus:ring-2 focus:ring-pink-500',
          isDark
            ? 'bg-gray-700 border-gray-600 text-gray-100 hover:bg-gray-600'
            : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50',
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="truncate text-sm">{selected?.title ?? title}</span>
        <ChevronDown
          className={classNames(
            'w-3 h-3 transition-transform',
            isOpen && 'rotate-180',
            isDark ? 'text-gray-300' : 'text-gray-500',
          )}
        />
      </button>

      {isOpen && (
        <div
          className={classNames(
            'absolute left-0 right-0 z-50 mt-1 w-full rounded-lg border shadow-xl max-h-60 overflow-auto overflow-x-hidden pointer-events-auto',
            isDark
              ? 'bg-gray-800 border-gray-700 text-gray-100'
              : 'bg-white border-gray-200 text-gray-900',
          )}
          role="listbox"
        >
          {MappingService.availableMaps.map((m) => {
            const isSelected = m.id === currentMapId;
            const isAvailable = isMapAvailable(m);
            return (
              <button
                key={m.id}
                disabled={!isAvailable}
                className={classNames(
                  'w-full text-left px-2 py-2 text-sm flex items-center justify-between',
                  !isAvailable && 'opacity-50 cursor-not-allowed',
                  isSelected
                    ? 'bg-pink-50 text-pink-700 dark:bg-pink-900/20 dark:text-pink-300'
                    : isDark
                      ? 'text-gray-200 hover:bg-gray-700'
                      : 'text-gray-800 hover:bg-gray-100',
                  !isAvailable && 'hover:bg-transparent',
                )}
                onClick={() => {
                  if (isAvailable) {
                    onChange(m.id);
                    onOpenChange(false);
                  }
                }}
                role="option"
                aria-selected={isSelected}
                aria-disabled={!isAvailable}
                title={!isAvailable ? 'Some variables not available for this model' : ''}
              >
                <span className="truncate">{m.title}</span>
                {isSelected && <span className="ml-2 text-pink-500">•</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface ModelDropdownProps {
  readonly isDark: boolean;
  readonly currentModelId: string;
  readonly onChange: (modelId: string, forecast: string) => void;
  readonly isOpen: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

function ModelDropdown({
  isDark,
  currentModelId,
  onChange,
  isOpen,
  onOpenChange,
}: ModelDropdownProps) {
  const [availableModelToForecasts, setAvailableModelToForecasts] = useState<
    Record<string, string[]>
  >({});
  const { isDemo } = useDemo();

  useEffect(() => {
    // Check if data is already cached from page load
    const cached = getCachedAvailableForecasts();
    if (cached) {
      setAvailableModelToForecasts(cached.forecasts ?? {});
    }

    // Subscribe to future updates (data will be fetched on page load)
    const unsubscribe = subscribeToAvailableForecasts((data) => {
      setAvailableModelToForecasts(data?.forecasts ?? {});
    });

    return unsubscribe;
  }, [isDemo]);
  const selected = getModelInfo(currentModelId);

  return (
    <div className="relative">
      <button
        onClick={() => onOpenChange(!isOpen)}
        className={classNames(
          'w-full px-2 py-1 rounded-md border flex items-center justify-between text-left transition-colors focus:outline-none focus:ring-2 focus:ring-pink-500',
          isDark
            ? 'bg-gray-700 border-gray-600 text-gray-100 hover:bg-gray-600'
            : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50',
        )}
      >
        <span className="truncate text-sm">{selected.label}</span>
        <ChevronDown
          className={classNames(
            'w-3 h-3 transition-transform',
            isOpen && 'rotate-180',
            isDark ? 'text-gray-300' : 'text-gray-500',
          )}
        />
      </button>
      {isOpen && (
        <div
          className={classNames(
            'absolute left-0 right-0 z-50 mt-1 w-full rounded-lg border shadow-xl max-h-60 overflow-auto overflow-x-hidden pointer-events-auto',
            isDark
              ? 'bg-gray-800 border-gray-700 text-gray-100'
              : 'bg-white border-gray-200 text-gray-900',
          )}
          role="listbox"
        >
          {AVAILABLE_MODELS.map((id) => {
            const isSelected = id === currentModelId;
            const info = getModelInfo(id);
            const modelForecasts = availableModelToForecasts[id] ?? [];
            const disabled = modelForecasts.length === 0;
            return (
              <button
                key={id}
                className={classNames(
                  'w-full text-left px-2 py-2 text-sm flex items-center justify-between',
                  disabled
                    ? isDark
                      ? 'text-gray-500 cursor-not-allowed'
                      : 'text-gray-400 cursor-not-allowed'
                    : isSelected
                      ? 'bg-pink-50 text-pink-700 dark:bg-pink-900/20 dark:text-pink-300'
                      : isDark
                        ? 'text-gray-200 hover:bg-gray-700'
                        : 'text-gray-800 hover:bg-gray-100',
                )}
                onClick={() => {
                  if (disabled) return;
                  const firstForecast = modelForecasts[0];
                  onChange(id, firstForecast);
                  onOpenChange(false);
                }}
                role="option"
                aria-selected={isSelected}
              >
                <span className="truncate">{info.label}</span>
                {disabled ? (
                  <span className="ml-2 text-xs text-gray-400">No forecasts</span>
                ) : (
                  isSelected && <span className="ml-2 text-pink-500">•</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function LayersPanel({
  title,
  isDark,
  allowSwitchDataSource,
  currentDataSource,
  currentToggles,
  activeToggleIds,
  onClose,
  onToggleChange,
  globalOverlayOpacity,
  setGlobalOverlayOpacity,
  currentMapId,
  setCurrentMap,
  setCurrentDataSource,
  onSettingsClick,
  availableVariables,
}: LayersPanelProps) {
  const [openDropdown, setOpenDropdown] = useState<'model' | 'base' | null>(null);
  return (
    <div
      className={`${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } shadow-sm border rounded-xl max-h-full overflow-y-auto min-w-64`}
    >
      {/* Header with close button and data source info */}
      <div className="p-2">
        {/* Top row: Weather model label and close button */}
        {allowSwitchDataSource && (
          <div className="flex items-center justify-between mb-1">
            <label className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Weather model
            </label>
            <button
              onClick={onClose}
              className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
                isDark
                  ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-300'
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
              }`}
              title="Close Layers"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {/* Dropdown row */}
        {allowSwitchDataSource && (
          <div className="space-x-2">
            <ModelDropdown
              isDark={isDark}
              currentModelId={currentDataSource.model}
              onChange={(modelId: string, forecast: string) =>
                setCurrentDataSource({ model: modelId, forecast })
              }
              isOpen={openDropdown === 'model'}
              onOpenChange={(open) =>
                setOpenDropdown(open ? 'model' : openDropdown === 'model' ? null : openDropdown)
              }
            />
          </div>
        )}
        {/* If no data source switching, just show close button */}
        {!allowSwitchDataSource && (
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
                isDark
                  ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-300'
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
              }`}
              title="Close Layers"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Separator */}
      <div className={`border-t ${isDark ? 'border-gray-600' : 'border-gray-200'}`} />
      <div className="pt-2 pl-2 pr-2">
        <label className={`text-xs mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Base layer
        </label>
        <BaseLayerDropdown
          isDark={isDark}
          currentMapId={currentMapId}
          title={title}
          onChange={(id: number) => {
            const m = MappingService.availableMaps.find((mm) => mm.id === id);
            if (m) setCurrentMap(m);
          }}
          isOpen={openDropdown === 'base'}
          onOpenChange={(open) =>
            setOpenDropdown(open ? 'base' : openDropdown === 'base' ? null : openDropdown)
          }
          availableVariables={availableVariables}
        />
      </div>

      <div className="p-4">
        <div className="space-y-3">
          {/* Global Overlay Opacity */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Overlay opacity
              </span>
              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {Math.round(globalOverlayOpacity * 100)}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={globalOverlayOpacity}
              onChange={(e) => setGlobalOverlayOpacity(Number(e.target.value))}
              className="w-full"
              style={{ accentColor: '#E34290' }}
            />
          </div>

          {currentToggles.map((toggle) => {
            const isAvailable = (toggle as any).available !== false;
            const isActive = activeToggleIds.has(toggle.title);
            return (
              <div key={toggle.title} className="flex items-center justify-between">
                <span
                  className={`text-sm font-medium ${
                    !isAvailable
                      ? 'text-gray-400 opacity-50'
                      : isDark
                        ? 'text-gray-300'
                        : 'text-gray-700'
                  }`}
                  title={!isAvailable ? 'Variable not available for this model' : ''}
                >
                  {toggle.title}
                </span>
                <button
                  onClick={() => isAvailable && onToggleChange(toggle.title, !isActive)}
                  disabled={!isAvailable}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                    isAvailable ? '' : 'cursor-not-allowed opacity-50'
                  } ${
                    isActive && isAvailable
                      ? 'bg-pink-500'
                      : isAvailable
                        ? isDark
                          ? 'bg-gray-600'
                          : 'bg-gray-200'
                        : 'bg-gray-300'
                  }`}
                  role="switch"
                  aria-checked={isActive}
                  aria-disabled={!isAvailable}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isActive ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            );
          })}

          {/* Settings Button */}
          <div className={`pt-3 mt-3 border-t ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
            <button
              onClick={onSettingsClick}
              className={`w-full flex items-center justify-center gap-2 p-2 rounded-lg transition-colors ${
                isDark
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              title="Performance Settings"
            >
              <Settings className="w-4 h-4" />
              <span className="text-sm font-medium">Settings</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
